import { useEffect, useState, type CSSProperties, type HTMLAttributes, type ReactNode, type RefObject } from 'react';
import { clsx } from 'clsx';
import { Icon } from './icons';
import { IconButton } from './buttons';
import { Badge } from './feedback';

export interface NavigationBarProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: string; subtitle?: ReactNode; leading?: ReactNode; trailing?: ReactNode; onBack?: () => void; backLabel?: string;
  largeTitle?: boolean; scrollOffset?: number; scrollContainer?: RefObject<HTMLElement | null>;
}
export function NavigationBar({ title, subtitle, leading, trailing, onBack, backLabel, largeTitle, scrollOffset,
  scrollContainer, className, style, ...props }: NavigationBarProps) {
  const [observedOffset, setObservedOffset] = useState(0);
  useEffect(() => {
    if (!largeTitle || scrollOffset !== undefined) return;
    const target = scrollContainer?.current ?? window;
    const update = () => setObservedOffset(target instanceof HTMLElement ? target.scrollTop : window.scrollY);
    update(); target.addEventListener('scroll', update, { passive: true });
    return () => target.removeEventListener('scroll', update);
  }, [largeTitle, scrollOffset, scrollContainer]);
  const collapse = Math.max(0, Math.min(1, (scrollOffset ?? observedOffset) / 52));
  return <header {...props} className={clsx('hylo-navigation-bar', 'hylo-glass', largeTitle && 'hylo-navigation-bar--large', className)}
    style={{ '--nav-collapse': collapse, ...style } as CSSProperties}>
    <div className="hylo-navigation-bar__toolbar">
      {onBack && <IconButton className="hylo-navigation-bar__back" name="chevron.left" label={backLabel ?? title} onClick={onBack} />}{leading}
      <div className="hylo-navigation-bar__identity"><h1>{title}</h1>{subtitle && <div className="hylo-navigation-bar__subtitle">{subtitle}</div>}</div>
      {trailing && <div className="hylo-navigation-bar__trailing">{trailing}</div>}
    </div>{largeTitle && <div className="hylo-navigation-bar__large-title" aria-hidden="true">{title}</div>}
  </header>;
}

export interface NavigationItem<T extends string = string> { id: T; label: string; icon: string; badge?: number; disabled?: boolean; shortcut?: string }
export interface TabBarProps<T extends string = string> { label: string; items: readonly NavigationItem<T>[]; value: T; onValueChange: (value: T) => void; className?: string }
export function TabBar<T extends string>({ label, items, value, onValueChange, className }: TabBarProps<T>) {
  return <nav aria-label={label} className={clsx('hylo-tab-bar', 'hylo-glass', className)}>{items.map(item => <button type="button" key={item.id}
    aria-current={item.id === value ? 'page' : undefined} disabled={item.disabled} onClick={() => onValueChange(item.id)}>
    <span className="hylo-tab-bar__glyph"><Icon name={item.icon} />{!!item.badge && <Badge count={item.badge} />}</span>
    <span className="hylo-tab-bar__label">{item.label}</span></button>)}</nav>;
}

export interface SidebarRailProps<T extends string = string> extends TabBarProps<T> { header?: ReactNode; footer?: ReactNode }
export function SidebarRail<T extends string>({ label, items, value, onValueChange, header, footer, className }: SidebarRailProps<T>) {
  return <nav aria-label={label} className={clsx('hylo-sidebar-rail', 'hylo-glass', className)}>{header}
    <div className="hylo-sidebar-rail__items">{items.map(item => <button type="button" key={item.id} title={`${item.label}${item.shortcut ? ` (${item.shortcut})` : ''}`}
      aria-label={item.label} aria-current={item.id === value ? 'page' : undefined} disabled={item.disabled} onClick={() => onValueChange(item.id)}>
      <Icon name={item.icon} />{!!item.badge && <Badge count={item.badge} />}</button>)}</div>
    {footer && <div className="hylo-sidebar-rail__footer">{footer}</div>}</nav>;
}
